// DashboardLeadAssignmentTable.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardBody, CardHeader } from 'reactstrap';
import { Users, UserCheck, UserX } from 'lucide-react';
import { fetchDashboardLeadAssignmentDistribution } from '../../services/dashboardService';
import logger from '../../utils/logger';

// Interface for assignment response items (matching your exact API response)
interface AssignmentResponseItem {
  count: number;
  assignedTo: string | null;
  assignedUserName?: string;
}

// Processed assignment item with additional UI fields
interface ProcessedAssignmentItem extends AssignmentResponseItem {
  percentage: number;
  isUnassigned: boolean;
}

// Component props - following EXACT same pattern as other dashboard components
interface DashboardLeadAssignmentTableProps {
  fromDate: string;
  toDate: string;
  organization: boolean;
  userId: string;
  selectedTeamId: string;
  title?: string;
}

/**
 * Helper function to process raw assignment data for UI display
 * Handles duplicate "Unassigned" entries and calculates percentages
 */
const processAssignmentData = (rawData: AssignmentResponseItem[]): ProcessedAssignmentItem[] => {
  logger.log('🔄 Processing assignment data:', rawData);
  
  if (!Array.isArray(rawData) || rawData.length === 0) {
    console.warn('⚠️ No assignment data to process');
    return [];
  }
  
  // ✅ FILTER: Ignore entries where assignedTo is null (old duplicate data)
  const filteredData = rawData.filter(item => {
    if (item.assignedTo === null) {
      logger.log('🗑️ Ignoring old null assignedTo entry:', item);
      return false; // Skip this entry
    }
    return true; // Keep this entry
  });
  
  logger.log('✅ After filtering out null assignedTo:', filteredData);
  
  // Rest of the processing remains the same
  const totalLeads = filteredData.reduce((sum, item) => sum + item.count, 0);
  
  const finalData = filteredData
    .map(item => ({
      ...item,
      percentage: totalLeads > 0 ? Math.round((item.count / totalLeads) * 100) : 0,
      isUnassigned: item.assignedUserName === 'Unassigned' || !item.assignedTo
    }))
    .sort((a, b) => {
      // Sort: assigned users first (by count desc), then unassigned
      if (a.isUnassigned && !b.isUnassigned) return 1;
      if (!a.isUnassigned && b.isUnassigned) return -1;
      return b.count - a.count;
    });
  
  logger.log('✅ Final processed data:', finalData);
  return finalData as ProcessedAssignmentItem[];
};

/**
 * Dashboard component that displays lead assignment distribution
 * Shows how many leads are assigned to each user vs unassigned
 * Follows EXACT same pattern as other dashboard components
 */
const DashboardLeadAssignmentTable: React.FC<DashboardLeadAssignmentTableProps> = ({
  fromDate,
  toDate,
  organization,
  userId,
  selectedTeamId,
  title = "Lead Assignment Distribution"
}) => {
  logger.log('🔄 DashboardLeadAssignmentTable rendered with params:', {
    fromDate, toDate, organization, userId, selectedTeamId
  });

  // State management
  const [rawAssignmentData, setRawAssignmentData] = useState<AssignmentResponseItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Process the raw data for display
  const processedData: ProcessedAssignmentItem[] = useMemo(() => {
    return processAssignmentData(rawAssignmentData);
  }, [rawAssignmentData]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!processedData.length) {
      return {
        totalLeads: 0,
        assignedCount: 0,
        unassignedCount: 0,
        totalUsers: 0,
        assignmentRate: 0
      };
    }

    const totalLeads = processedData.reduce((sum, item) => sum + item.count, 0);
    const unassignedCount = processedData
      .filter(item => item.isUnassigned)
      .reduce((sum, item) => sum + item.count, 0);
    const assignedCount = totalLeads - unassignedCount;
    const totalUsers = processedData.filter(item => !item.isUnassigned).length;
    const assignmentRate = totalLeads > 0 ? Math.round((assignedCount / totalLeads) * 100) : 0;

    logger.log('📊 Summary stats calculated:', {
      totalLeads, assignedCount, unassignedCount, totalUsers, assignmentRate
    });

    return {
      totalLeads,
      assignedCount,
      unassignedCount,
      totalUsers,
      assignmentRate
    };
  }, [processedData]);

  // Fetch assignment data when parameters change - following dashboard service pattern
  useEffect(() => {
    const fetchData = async () => {
      logger.log('🔍 Fetching assignment data...');
      setLoading(true);
      setError(null);

      try {
        // Call dashboard service following the same pattern as other dashboard components
        const response = await fetchDashboardLeadAssignmentDistribution(
          fromDate,
          toDate,
          organization,
          userId,
          selectedTeamId
        );
        
        logger.log('📡 Assignment API response:', response);
        
        if (response && response.data && response.data.status && Array.isArray(response.data.data)) {
          logger.log('✅ Assignment data loaded:', response.data.data);
          setRawAssignmentData(response.data.data);
        } else if (response && Array.isArray(response.data)) {
          // Handle direct array response
          logger.log('✅ Assignment data loaded (direct array):', response.data);
          setRawAssignmentData(response.data);
        } else {
          console.warn('⚠️ Invalid response format:', response);
          throw new Error('Invalid response format from API');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch assignment data';
        console.error('❌ Error fetching assignment data:', err);
        setError(errorMessage);
        setRawAssignmentData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fromDate, toDate, organization, userId, selectedTeamId]);

  // Render loading state
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <div className="d-flex align-items-center">
            <Users size={20} className="me-2" />
            <span className="fw-bold text-dark fm-gilroy">{title}</span>
          </div>
        </CardHeader>
        <CardBody>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading assignment data...</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  // Render error state
  if (error) {
    return (
      <Card>
        <CardHeader>
          <div className="d-flex align-items-center">
            <Users size={20} className="me-2" />
            <span className="fw-bold text-dark fm-gilroy">{title}</span>
          </div>
        </CardHeader>
        <CardBody>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="text-danger mb-2">
              <Users size={48} style={{ opacity: 0.5 }} />
            </div>
            <h6 className="text-danger">Error Loading Data</h6>
            <p className="text-muted">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="btn btn-outline-primary btn-sm"
            >
              Retry
            </button>
          </div>
        </CardBody>
      </Card>
    );
  }

  // Render empty state
  if (!processedData.length) {
    return (
      <Card>
        <CardHeader>
          <div className="d-flex align-items-center">
            <Users size={20} className="me-2" />
            <span className="fw-bold text-dark fm-gilroy">{title}</span>
          </div>
        </CardHeader>
        <CardBody>
          <h2 className="no_data_found">No assignment data available.</h2>
        </CardBody>
      </Card>
    );
  }

  // Main render
  return (
    <Card>
      <CardHeader>
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <Users size={20} className="me-2" />
            <span className="fw-bold text-dark fm-gilroy">{title}</span>
          </div>
          <div className="d-flex align-items-center text-sm text-muted">
            <span>Assignment Rate: <strong>{summaryStats.assignmentRate}%</strong></span>
          </div>
        </div>
      </CardHeader>
      
      <CardBody className="p-0">
        {/* Summary Statistics Row */}
        <div className="d-flex justify-content-around py-3" style={{ backgroundColor: '#f8f9fa' }}>
          <div className="text-center">
            <div className="h5 mb-0 fw-bold">{summaryStats.totalLeads.toLocaleString()}</div>
            <small className="text-muted fw-semibold">Total Leads</small>
          </div>
          <div className="text-center">
            <div className="h5 mb-0 fw-bold text-success">{summaryStats.assignedCount.toLocaleString()}</div>
            <small className="text-muted fw-semibold">Assigned</small>
          </div>
          <div className="text-center">
            <div className="h5 mb-0 fw-bold text-danger">{summaryStats.unassignedCount.toLocaleString()}</div>
            <small className="text-muted fw-semibold">Unassigned</small>
          </div>
          <div className="text-center">
           
          </div>
        </div>

        {/* Assignment Table */}
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead style={{ backgroundColor: '#f1f5f9' }}>
              <tr>
                <th className="fw-bold text-dark border-0">Assigned To</th>
                <th className="fw-bold text-dark border-0 text-end">Count</th>
                <th className="fw-bold text-dark border-0 text-end">Percentage</th>
               
              </tr>
            </thead>
           <tbody>
  {processedData.map((item, index) => {
    return (
      <tr key={item.assignedTo || `unassigned-${index}`}>
        <td className="border-0">
          <div className="d-flex align-items-center">
            <div 
              className="rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{
                width: '36px',
                height: '36px',
                backgroundColor: item.isUnassigned ? '#dc3545' : '#007bff',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              {item.isUnassigned ? (
                <UserX size={18} />
              ) : (
                item.assignedUserName?.charAt(0).toUpperCase() || '?'
              )}
            </div>
            <div>
              <div className="fw-semibold text-dark">
                {item.assignedUserName || 'Unassigned'}
              </div>
              {/* ❌ REMOVED: ID display
              {item.assignedTo && (
                <small className="text-muted">
                  ID: {item.assignedTo.slice(-8)}
                </small>
              )}
              */}
            </div>
          </div>
        </td>
        <td className="border-0 text-end">
          <span 
            className="badge fs-6 px-3 py-2"
            style={{
              backgroundColor: item.isUnassigned ? '#dc3545' : '#007bff',
              color: 'white'
            }}
          >
            {item.count.toLocaleString()}
          </span>
        </td>
        <td className="border-0 text-end">
          <span className="fw-bold fs-6">{item.percentage}%</span>
        </td>
        {/* ❌ REMOVED: Status column data
        <td className="border-0 text-end">
          <span 
            className={`badge ${item.isUnassigned ? 'bg-danger' : 'bg-success'}`}
          >
            {item.isUnassigned ? (
              <>
                <UserX size={12} className="me-1" />
                Unassigned
              </>
            ) : (
              <>
                <UserCheck size={12} className="me-1" />
                Assigned
              </>
            )}
          </span>
        </td>
        */}
      </tr>
    );
  })}
</tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
};

export default DashboardLeadAssignmentTable;